import React, { useEffect, useState } from 'react'
import DashboardHeader from "../layouts/dashboard_header";
import SideMenu from "../layouts/dashboard_menu";
import DashboardFooter from "../layouts/dashboard_footer";
import { buildApiUrl, useBaseUrl } from "../../utils/utils";
import localforage from "localforage";
import Swal from "sweetalert2";

interface ApiKeyRow {
    id: number;
    name: string;
    key: string;
    active: boolean;
    created: string;
    last_used: string | null;
}

const SaaSApi: React.FC = () => {
    const baseUrl = useBaseUrl();
    const [clinicId, setClinicId] = useState<number | null>(null);
    const [staffId, setStaffId] = useState<number | null>(null);
    const [keys, setKeys] = useState<ApiKeyRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [keyName, setKeyName] = useState('');
    const [generating, setGenerating] = useState(false);

    useEffect(() => {
        document.title = "Uzima API - Management"
        localforage.getItem("clinic_id").then((cid: any) => setClinicId(cid ? Number(cid) : null));
        localforage.getItem("staff_id").then((sid: any) => setStaffId(sid ? Number(sid) : null));
    }, [])

    const loadKeys = async () => {
        if (!clinicId) return;
        setLoading(true);
        try {
            const res = await fetch(buildApiUrl(baseUrl, `facilities/api-keys/?clinic_id=${clinicId}`));
            const data = await res.json();
            if (data.status === 'success') setKeys(data.data || []);
        } catch { /* leave list as-is on failure */ } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadKeys(); }, [clinicId]); // eslint-disable-line react-hooks/exhaustive-deps

    const generateKey = async () => {
        if (!keyName.trim()) {
            Swal.fire({ icon: 'warning', title: 'Name Required', text: 'Give this key a label so you can identify it later.' });
            return;
        }
        setGenerating(true);
        try {
            const res = await fetch(buildApiUrl(baseUrl, `facilities/api-keys/`), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ clinic_id: clinicId, staff_id: staffId, name: keyName }),
            });
            const data = await res.json();
            if (res.ok && data.status === 'success') {
                setKeyName('');
                loadKeys();
                await Swal.fire({
                    icon: 'success', title: 'API Key Generated',
                    html: `<p>Copy this key now — it will not be shown again:</p>
                           <div class="input-group"><input readonly class="form-control" value="${data.data.key}" onclick="this.select()"></div>`,
                });
            } else {
                Swal.fire({ icon: 'error', title: 'Error', text: data.message || 'Failed to generate key.' });
            }
        } catch {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Network error while generating key.' });
        } finally {
            setGenerating(false);
        }
    };

    const revokeKey = async (key: ApiKeyRow) => {
        const confirm = await Swal.fire({
            title: `Revoke "${key.name}"?`, text: 'Any integration using this key will stop working immediately.',
            icon: 'warning', showCancelButton: true, confirmButtonColor: '#dc3545', confirmButtonText: 'Yes, revoke it',
        });
        if (!confirm.isConfirmed) return;
        try {
            const res = await fetch(buildApiUrl(baseUrl, `facilities/api-keys/${key.id}/revoke`), { method: 'PATCH' });
            if (res.ok) loadKeys();
        } catch { /* best-effort */ }
    };

    return (
        <>
            <DashboardHeader />
            <SideMenu />
            <div className={"vertical-overlay"}></div>
            <div className={"main-content"}>
                <div className={"page-content"}>
                    <div className="container-fluid">
                        <div className="row">
                            <div className="col-12">
                                <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                                    <h4 className="mb-sm-0">Uzima API Management</h4>
                                    <div className="page-title-right">
                                        <ol className="breadcrumb m-0">
                                            <li className="breadcrumb-item"><a href="/institution/home">Dashboards</a></li>
                                            <li className="breadcrumb-item active">API Management</li>
                                        </ol>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-lg-8">
                                <div className="card">
                                    <div className="card-header">
                                        <h5 className="card-title mb-0">API Keys</h5>
                                    </div>
                                    <div className="card-body">
                                        {loading ? (
                                            <div className="text-center py-5">
                                                <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div>
                                            </div>
                                        ) : keys.length === 0 ? (
                                            <div className="text-center py-5">
                                                <i className="ri-key-2-line" style={{ fontSize: '2.5rem' }}></i>
                                                <h5 className="mt-3">No API keys yet</h5>
                                                <p className="text-muted">Generate a key to authenticate programmatic access for your facility.</p>
                                            </div>
                                        ) : (
                                            <div className="table-responsive">
                                                <table className="table table-hover align-middle mb-0">
                                                    <thead>
                                                        <tr><th>Name</th><th>Key</th><th>Created</th><th>Last Used</th><th>Status</th><th>Action</th></tr>
                                                    </thead>
                                                    <tbody>
                                                        {keys.map(key => (
                                                            <tr key={key.id}>
                                                                <td>{key.name}</td>
                                                                <td><code>{key.key}</code></td>
                                                                <td>{new Date(key.created).toLocaleDateString()}</td>
                                                                <td>{key.last_used ? new Date(key.last_used).toLocaleDateString() : 'Never'}</td>
                                                                <td>
                                                                    <span className={`badge ${key.active ? 'bg-success' : 'bg-secondary'}`}>{key.active ? 'Active' : 'Revoked'}</span>
                                                                </td>
                                                                <td>
                                                                    {key.active && (
                                                                        <button className="btn btn-sm btn-outline-danger" onClick={() => revokeKey(key)}>Revoke</button>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="col-lg-4">
                                <div className="card">
                                    <div className="card-header">
                                        <h5 className="card-title mb-0">Generate New Key</h5>
                                    </div>
                                    <div className="card-body">
                                        <div className="mb-3">
                                            <label className="form-label">Key Label</label>
                                            <input type="text" className="form-control" placeholder="e.g. Billing System Integration"
                                                   value={keyName} onChange={(e) => setKeyName(e.target.value)} />
                                        </div>
                                        <button className="btn btn-primary w-100" disabled={generating} onClick={generateKey}>
                                            {generating ? 'Generating...' : 'Generate Key'}
                                        </button>
                                        <p className="text-muted small mt-3 mb-0">
                                            The full key is only shown once, right after generation. Store it securely.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <DashboardFooter />
            </div>
        </>
    )
}

export default SaaSApi
